import { useState, type ReactNode } from 'react';
import { Sparkles, X, ArrowUp, Paperclip, Mic, Camera, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn';

export interface AliceMessage {
  id: string;
  role: 'user' | 'assistant';
  content: ReactNode;
}

export interface AliceSuggestion {
  id: string;
  label: string;
  icon?: ReactNode;
}

export interface AliceActionButton {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Quiet by default; set tone 'accent' for the single primary action. */
  tone?: 'neutral' | 'accent';
  onClick?: () => void;
}

/**
 * AliceDrawer — the full production expanded assistant panel.
 *
 * **Default variant (`variant="docked"`)** is the production experience: right-docked
 * drawer with a full feature set matching the live AlicePanel. Wire all behavior through
 * props — the drawer is presentation-only so legacy and modern share the same underlying
 * Alice context/service.
 *
 * **`variant="floating"`** is a compact prototype / design-lab variant. It renders a
 * smaller floating card with a simplified layout. Use for demos and the design lab only —
 * do NOT use as the default expanded experience in production.
 *
 * Production feature checklist (docked):
 *   - Header: avatar, name, status badge, context-aware subtitle, daily token display
 *   - Minimize control (→ AliceLauncher), optional close control
 *   - Context engine section with expandable detail and item count
 *   - Quick-action chips (Plan my day, Find related work, etc.)
 *   - Chat message stream with empty-state card
 *   - Contextual action buttons (accent + ghost) after assistant turn
 *   - Suggestion chips below messages
 *   - Bottom composer: textarea, mic / attach / camera icons, model selector,
 *     "Cmd/Ctrl + Enter to send" hint, Send button
 *
 * Confirmation-before-write: the design-system component exposes the wire-up points
 * (props) but the confirmation UI (CopilotWriteConfirmCard) lives in the app layer.
 * Pass any pending-write content as a `ReactNode` via the `messages` array or as an
 * additional child in the message stream — the drawer renders whatever it receives.
 */
export function AliceDrawer({
  name = 'Alice',
  avatarSrc,
  status = 'online',
  statusLabel,
  subtitle,
  metaLabel,
  contextLabel,
  contextItemCount,
  messages = [],
  quickActions,
  suggestions = [],
  actions = [],
  composerValue,
  onComposerChange,
  onSend,
  onRunSuggestion,
  onClose,
  onMinimize,
  variant = 'docked',
  position = 'absolute',
  modelLabel,
  onOpenCamera,
  className,
}: {
  name?: string;
  avatarSrc?: string;
  status?: 'online' | 'thinking' | 'offline';
  statusLabel?: string;
  /** Context-aware subtitle below the assistant name, e.g. "Context-aware help for this workspace." */
  subtitle?: string;
  /** Daily token/budget display, e.g. "$4.98 left today". Pass null to hide. */
  metaLabel?: string | null;
  /** Label shown in the context chip header. */
  contextLabel?: ReactNode;
  /** Number of context items attached (shown as "· N item(s)"). */
  contextItemCount?: number;
  messages?: AliceMessage[];
  /** Quick-action chips shown above the message stream (Plan my day, Find related work, etc.). */
  quickActions?: AliceSuggestion[];
  suggestions?: AliceSuggestion[];
  actions?: AliceActionButton[];
  composerValue?: string;
  onComposerChange?: (value: string) => void;
  onSend?: (value: string) => void;
  onRunSuggestion?: (id: string) => void;
  onClose?: () => void;
  /** Collapse to the launcher pill (different from close). */
  onMinimize?: () => void;
  /**
   * `"docked"` — production full-feature drawer, fills its container.
   * `"floating"` — **compact prototype / demo-only** floating card. Not for production use.
   */
  variant?: 'docked' | 'floating';
  position?: 'fixed' | 'absolute';
  /** Model selector label, e.g. "Pro — GPT-5.4". Omit to hide the selector. */
  modelLabel?: string;
  /** Handler for the camera capture button. */
  onOpenCamera?: () => void;
  className?: string;
}) {
  const [contextExpanded, setContextExpanded] = useState(false);
  const floating = variant === 'floating';
  const resolvedStatusLabel =
    statusLabel ?? (status === 'thinking' ? 'Thinking…' : status === 'offline' ? 'Offline' : 'Online');

  /* ── Floating / compact prototype variant ─────────────────────────────── */
  if (floating) {
    return (
      <section
        aria-label={`${name} assistant (compact)`}
        className={cn(
          'z-30 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[var(--radius)] border border-border bg-card text-foreground shadow-[var(--mui-shadow-pop)]',
          position === 'fixed'
            ? 'fixed bottom-5 right-5 h-[34rem] max-h-[calc(100vh-2.5rem)]'
            : 'absolute bottom-4 right-4 h-[32rem]',
          className,
        )}
      >
        {/* Compact header */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary">
            {avatarSrc ? (
              <img src={avatarSrc} alt={`${name} avatar`} className="h-full w-full object-cover" />
            ) : (
              <Sparkles className="h-4 w-4" aria-hidden />
            )}
            <span
              aria-hidden
              className={cn(
                'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card',
                status === 'online' && 'bg-[#16a34a]',
                status === 'thinking' && 'bg-warning',
                status === 'offline' && 'bg-muted-foreground/50',
              )}
            />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-foreground">{name}</div>
            <div className="truncate text-[11px] text-muted-foreground">{resolvedStatusLabel}</div>
          </div>
          {onMinimize && (
            <button
              type="button"
              onClick={onMinimize}
              aria-label="Minimize assistant"
              className="flex h-7 w-7 items-center justify-center rounded-[calc(var(--radius)-6px)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close assistant"
              className="flex h-7 w-7 items-center justify-center rounded-[calc(var(--radius)-6px)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>

        {/* Message stream */}
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.length === 0 ? (
            <AliceEmptyState name={name} />
          ) : (
            messages.map((message) => <MessageBubble key={message.id} message={message} />)
          )}
        </div>

        {/* Actions + suggestions */}
        {actions.length > 0 && <ActionRow actions={actions} />}
        {suggestions.length > 0 && <SuggestionRow suggestions={suggestions} onSelect={onRunSuggestion} />}

        {/* Composer */}
        <CompactComposer
          name={name}
          composerValue={composerValue}
          onComposerChange={onComposerChange}
          onSend={onSend}
        />
      </section>
    );
  }

  /* ── Docked / production drawer ────────────────────────────────────────── */
  return (
    <section
      aria-label={`${name} assistant`}
      className={cn('flex h-full w-full flex-col bg-card text-foreground', className)}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 border-b border-border px-4 py-3">
        {/* Avatar */}
        <span className="relative mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary">
          {avatarSrc ? (
            <img src={avatarSrc} alt={`${name} avatar`} className="h-full w-full object-cover" />
          ) : (
            <Sparkles className="h-5 w-5" aria-hidden />
          )}
          <span
            aria-hidden
            className={cn(
              'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card',
              status === 'online' && 'bg-[#16a34a]',
              status === 'thinking' && 'bg-warning',
              status === 'offline' && 'bg-muted-foreground/50',
            )}
          />
        </span>

        {/* Name / status / subtitle / meta */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-foreground">{name}</span>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                status === 'online' && 'bg-emerald-500/10 text-emerald-700',
                status === 'thinking' && 'bg-amber-500/10 text-amber-700',
                status === 'offline' && 'bg-muted text-muted-foreground',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  status === 'online' && 'bg-[#16a34a]',
                  status === 'thinking' && 'bg-amber-500',
                  status === 'offline' && 'bg-muted-foreground/50',
                )}
              />
              {resolvedStatusLabel}
            </span>
          </div>
          {subtitle && (
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{subtitle}</p>
          )}
          {metaLabel && (
            <p className="mt-0.5 text-[11px] text-muted-foreground">{metaLabel}</p>
          )}
        </div>

        {/* Controls: minimize (→ launcher) + optional close */}
        <div className="mt-0.5 flex items-center gap-1">
          {onMinimize && (
            <button
              type="button"
              onClick={onMinimize}
              aria-label="Minimize to launcher"
              title="Minimize to launcher"
              className="flex h-7 w-7 items-center justify-center rounded-[calc(var(--radius)-6px)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close assistant"
              className="flex h-7 w-7 items-center justify-center rounded-[calc(var(--radius)-6px)] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {/* ── Context engine section ──────────────────────────────────────────── */}
      {contextLabel && (
        <div className="border-b border-border">
          <button
            type="button"
            onClick={() => setContextExpanded((v) => !v)}
            aria-expanded={contextExpanded}
            className="flex w-full items-center gap-2 px-4 py-2 text-left"
          >
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/70" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
              {contextLabel}
              {contextItemCount != null && (
                <span className="text-muted-foreground/70"> · {contextItemCount} item{contextItemCount === 1 ? '' : 's'}</span>
              )}
            </span>
            <ChevronDown
              className={cn('h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform', contextExpanded && 'rotate-180')}
              aria-hidden
            />
          </button>
          {contextExpanded && (
            <div className="border-t border-border bg-background/50 px-4 py-3">
              <p className="text-[11px] text-muted-foreground">
                Page context is attached to your next message. Alice uses it to give relevant answers without
                you needing to paste record details manually.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Quick-action chips ──────────────────────────────────────────────── */}
      {quickActions && quickActions.length > 0 && (
        <div className="border-b border-border px-4 py-3">
          <div className="flex flex-wrap gap-1.5">
            {quickActions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => onRunSuggestion?.(action.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5"
              >
                {action.icon ?? <Sparkles className="h-3 w-3 flex-shrink-0 text-primary" aria-hidden />}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Message stream ──────────────────────────────────────────────────── */}
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <AliceEmptyState name={name} />
        ) : (
          messages.map((message) => <MessageBubble key={message.id} message={message} />)
        )}
      </div>

      {/* ── Contextual action buttons ───────────────────────────────────────── */}
      {actions.length > 0 && <ActionRow actions={actions} />}

      {/* ── Suggestion chips ────────────────────────────────────────────────── */}
      {suggestions.length > 0 && <SuggestionRow suggestions={suggestions} onSelect={onRunSuggestion} />}

      {/* ── Composer ────────────────────────────────────────────────────────── */}
      <form
        className="border-t border-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (composerValue?.trim()) onSend?.(composerValue.trim());
        }}
      >
        {/* Toolbar: mic, attach, camera, model selector */}
        <div className="mb-2 flex items-center gap-1.5">
          <button
            type="button"
            aria-label="Voice input"
            className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Mic className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Attach file"
            className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Paperclip className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Capture photo"
            onClick={onOpenCamera}
            className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Camera className="h-3.5 w-3.5" aria-hidden />
          </button>
          {modelLabel && (
            <button
              type="button"
              aria-label="Change AI model"
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              <Sparkles className="h-3 w-3 text-primary" aria-hidden />
              <span className="max-w-[140px] truncate">{modelLabel}</span>
              <ChevronDown className="h-3 w-3" aria-hidden />
            </button>
          )}
        </div>

        {/* Textarea + hint + send */}
        <div className="rounded-[var(--radius)] border border-border bg-background focus-within:ring-1 focus-within:ring-ring">
          <textarea
            rows={3}
            value={composerValue ?? ''}
            onChange={(e) => onComposerChange?.(e.target.value)}
            placeholder={`Ask ${name} to draft, summarize, find, or update…`}
            className="min-h-[72px] max-h-40 w-full resize-none rounded-t-[var(--radius)] bg-transparent px-3 py-2.5 text-[13px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if (composerValue?.trim()) onSend?.(composerValue.trim());
              }
            }}
          />
          <div className="flex items-center justify-between gap-3 border-t border-border/70 px-2.5 py-2">
            <span className="text-[11px] text-muted-foreground">Cmd/Ctrl + Enter to send</span>
            <button
              type="submit"
              aria-label="Send"
              disabled={!composerValue?.trim()}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:opacity-40"
            >
              <ArrowUp className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

/* ── Internal sub-components ────────────────────────────────────────────── */

function AliceEmptyState({ name }: { name: string }) {
  return (
    <div className="rounded-lg border border-border bg-background/70 px-3 py-3 text-[12px] text-muted-foreground">
      <p className="font-medium text-foreground">Ask {name} about tasks, records, or next actions.</p>
      <p className="mt-1">Writes still require confirmation before anything changes in Airtable.</p>
    </div>
  );
}

function MessageBubble({ message }: { message: AliceMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-[var(--radius)] px-3 py-2 text-[13px] leading-relaxed',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        {message.content}
      </div>
    </div>
  );
}

function ActionRow({ actions }: { actions: AliceActionButton[] }) {
  return (
    <div className="flex flex-wrap gap-1.5 border-t border-border px-4 py-2.5">
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          onClick={action.onClick}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors',
            action.tone === 'accent'
              ? 'bg-primary/[0.08] text-primary hover:bg-primary/[0.14]'
              : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          {action.icon}
          {action.label}
        </button>
      ))}
    </div>
  );
}

function SuggestionRow({
  suggestions,
  onSelect,
}: {
  suggestions: AliceSuggestion[];
  onSelect?: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5 border-t border-border px-4 py-2.5">
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.id}
          type="button"
          onClick={() => onSelect?.(suggestion.id)}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-[12px] text-foreground transition-colors hover:bg-muted"
        >
          {suggestion.icon}
          {suggestion.label}
        </button>
      ))}
    </div>
  );
}

function CompactComposer({
  name,
  composerValue,
  onComposerChange,
  onSend,
}: {
  name: string;
  composerValue?: string;
  onComposerChange?: (value: string) => void;
  onSend?: (value: string) => void;
}) {
  return (
    <form
      className="border-t border-border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (composerValue?.trim()) onSend?.(composerValue.trim());
      }}
    >
      <div className="flex items-end gap-2 rounded-[var(--radius)] border border-border bg-background px-2.5 py-2 focus-within:ring-2 focus-within:ring-ring">
        <button
          type="button"
          aria-label="Attach"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Paperclip className="h-4 w-4" aria-hidden />
        </button>
        <textarea
          rows={1}
          value={composerValue ?? ''}
          onChange={(e) => onComposerChange?.(e.target.value)}
          placeholder={`Message ${name}…`}
          className="max-h-28 min-h-[1.5rem] flex-1 resize-none bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (composerValue?.trim()) onSend?.(composerValue.trim());
            }
          }}
        />
        <button
          type="button"
          aria-label="Voice input"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Mic className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="submit"
          aria-label="Send"
          disabled={!composerValue?.trim()}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:opacity-40"
        >
          <ArrowUp className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </form>
  );
}
